import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Footer from "../Components/Footer";

export default function PrivacyPolicy() {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(
    localStorage.getItem("lang") ? (localStorage.getItem("lang") === "en" ? "ar" : "en") : "ar"
  );

  useEffect(() => {
    const saved = localStorage.getItem("lang") || "en";
    i18n.changeLanguage(saved);
    const newDir = saved === "ar" ? "rtl" : "ltr";
    document.documentElement.dir = newDir;
    document.documentElement.lang = saved;
    setLanguage(saved === "en" ? "ar" : "en");
  }, [i18n]);

  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "ar" : "en";
    localStorage.setItem("lang", newLang);
    i18n.changeLanguage(newLang);
    const newDir = newLang === "ar" ? "rtl" : "ltr";
    document.documentElement.dir = newDir;
    document.documentElement.lang = newLang;
    setLanguage(newLang === "en" ? "ar" : "en");
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-gray-900">
      {/* Legal Page Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/images/orb-logo.png" alt="Smart Container Logo" className="h-10 md:h-12" />
            <span className="font-semibold text-lg hidden sm:block text-[#209163]">Smart Container</span>
          </Link>

          <div className="flex items-center gap-3 md:gap-4">
            <Link
              to="/"
              className="hidden md:inline text-sm font-medium text-gray-700 hover:text-[#209163] transition-colors"
            >
              {t("back-to-home")}
            </Link>

            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border rounded-md hover:bg-gray-50 transition"
              aria-label="Toggle language"
            >
              <img src="/images/lang-icon.png" alt="Language" className="w-4 h-4" />
              <span className="uppercase">{language}</span>
            </button>

            <Link
              to="/"
              className="md:hidden text-xs px-3 py-1.5 rounded bg-[#209163] text-white font-medium hover:bg-[#187a52]"
            >
              {t("back-to-home").replace("← ", "")}
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 py-8 md:py-12 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#209163] mb-2">
            {t("privacy-policy-title")}
          </h1>
          <p className="text-sm text-gray-500">{t("last-updated")}</p>
        </div>

        <div className="prose prose-gray max-w-none text-[15px] leading-relaxed space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
            <p>
              Orb ("we", "us", or "our") operates the Smart Container website and mobile application
              (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your
              information when you use our Service. By using the Service, you consent to the data practices
              described in this policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Information We Collect</h2>
            <p>We may collect the following types of information:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li><strong>Personal Information:</strong> Name, phone number, email address, and commercial registration details (for partners).</li>
              <li><strong>Location Data:</strong> Precise location (with your permission) to show you the nearest Smart Containers.</li>
              <li><strong>Financial Information:</strong> Bank account details required to transfer recycling rewards.</li>
              <li><strong>Usage &amp; Device Data:</strong> App usage, device type, IP address, browser type, and interaction data.</li>
              <li><strong>Photos &amp; Submissions:</strong> Images or details of plastic materials you submit for recycling verification.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Provide and maintain the Service (locate containers, process submissions and rewards)</li>
              <li>Verify and credit recycling rewards to your bank account</li>
              <li>Communicate with you (notifications, support, partner updates)</li>
              <li>Improve the app, detect fraud, and analyze usage trends</li>
              <li>Comply with legal obligations and enforce our Terms</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Sharing of Information</h2>
            <p>We do not sell your personal data. We may share information with:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Service providers (hosting, analytics, payment processors) under confidentiality agreements</li>
              <li>Partner organizations that host Smart Containers (limited location and submission data)</li>
              <li>Law enforcement or regulators when required by law</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Data Security &amp; Retention</h2>
            <p>
              We implement industry-standard security measures (encryption, access controls) to protect your data.
              Bank details are tokenized and never stored in plaintext. We retain personal data only as long as
              necessary to provide the Service and meet legal requirements.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Your Rights</h2>
            <p>
              Depending on your location, you may have the right to access, correct, delete, or restrict
              processing of your personal data. You can also withdraw consent for location tracking at any time
              via your device settings. Contact us to exercise these rights.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The "Last Updated" date at the top indicates
              the latest version. Continued use of the Service after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy, please reach out through the Contact Us form on
              our website or email <a href="mailto:support@orb.sa" className="text-[#209163] underline">support@orb.sa</a>.
            </p>
          </section>
        </div>

        <div className="mt-10 pt-6 border-t text-xs text-gray-500">
          This policy applies to both the website and the Smart Container mobile application.
        </div>
      </main>

      <Footer />
    </div>
  );
}
